"use client";

import { useIsMobile } from "@/hooks/useIsMobile";
import { type FC, useRef, useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ProgramStructure2Data, ProgramStructure2DataSchema } from "./_schema";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { RootState } from "@/store/store";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { cn } from "@/lib/utils";
import { TitleSection } from "./_title";
import { Description } from "./_description";
import { CreditsAndSemesterTabs } from "./_credits_semester";
import { CategoryDropdowns } from "./_categories";
import { SubjectsTable } from "./_subjects";
import { ToolsAndSkillsSection } from "./_tools_skills";
import { DownloadButton } from "./_download_button";
import { ProgramStructure2Variant2 } from "./_variant2";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

export const ProgramStructure2: FC<any> = (props) => {
    const actualData = props?.data?.data || props?.data || props;
    const dispatch = useDispatch();
    const sectionRef = useRef<HTMLElement>(null);
    const [activeSemester, setActiveSemester] = useState(0);
    const [expandedElective, setExpandedElective] = useState<number | null>(null);
    const [openDropdown, setOpenDropdown] = useState<number | null>(null);
    const [selectedItemsByCategory, setSelectedItemsByCategory] = useState<Record<number, number>>({});
    // Reveal animations should play only on the first scroll-in. Once the user switches
    // semester or category, the subjects table swaps content instantly instead of
    // re-running the reveal each time.
    const [userInteracted, setUserInteracted] = useState(false);

    const isMobile = useIsMobile();
    const brochureUrl = useSelector(
        (state: RootState) => state.globalslice?.course?.brochure || ""
    );

    const parsedResult = useMemo(() => ProgramStructure2DataSchema.safeParse(actualData), [actualData]);

    if (!parsedResult.success) {
        console.error("ProgramStructure2 validation failed", parsedResult.error);
        return null;
    }

    const parsedData: ProgramStructure2Data = parsedResult.data;

    const isAnimationEnabled =
        parsedData.enable_animation === true || parsedData.enable_animation === 1;

    // Limit to maximum 6 semesters
    const semesters = parsedData.semesters?.slice(0, 6) || [];
    
    // Get subjects for active semester and selected categories
    const activeSubjects = useMemo(() => {
        const semesterId = semesters[activeSemester]?.id;
        
        return parsedData.subjects?.filter(subject => {
            // First, filter by semester
            if (subject.semester_id !== semesterId) {
                return false;
            }
            
            // If subject doesn't have a category_id or category_item_id, always show it
            if (!subject.category_id || !subject.category_item_id) {
                return true;
            }
            
            // If subject has a category_item_id, check if it matches the selected item for that category
            const selectedItemId = selectedItemsByCategory[subject.category_id as number];
            
            // If no item is selected for this category yet, show all subjects for that category
            if (selectedItemId === undefined) {
                return true;
            }
            
            // Only show subjects that match the selected category item
            return subject.category_item_id === selectedItemId;
        }) || [];
    }, [parsedData.subjects, semesters, activeSemester, selectedItemsByCategory]);

    // Calculate total credits for active semester
    const totalCredits = useMemo(() => {
        return activeSubjects.reduce((sum, subject) => sum + (subject.credits || 0), 0);
    }, [activeSubjects]);

    // Get categories for active semester
    const activeCategories = useMemo(() => {
        const semester = semesters[activeSemester];
        return semester?.categories?.filter(cat => cat.status === "1") || [];
    }, [semesters, activeSemester]);

    // Set default selections when semester changes
    useEffect(() => {
        if (activeCategories.length > 0) {
            const defaultSelections: Record<number, number> = {};
            activeCategories.forEach(category => {
                if (category.items && category.items.length > 0) {
                    const firstItem = category.items[0];
                    if (firstItem.id) {
                        defaultSelections[category.id as number] = firstItem.id as number;
                    }
                }
            });
            setSelectedItemsByCategory(defaultSelections);
        } else {
            // Clear selections if no categories
            setSelectedItemsByCategory({});
        }
    }, [activeSemester]); // Only run when semester changes, not when categories change

    // Reset dropdown when semester changes
    useEffect(() => {
        setOpenDropdown(null);
    }, [activeSemester]);

    const handleCtaClick = () => {
        dispatch(
            handleOpenLeaddrop({
                variant: LEAD_DROP_VARIANTS.DOWNLOAD,
                downloadLink: brochureUrl,
            })
        );
    };

    // Switching semester counts as interaction — suppress the reveal animation from here on.
    const handleSemesterChange = (index: number) => {
        setUserInteracted(true);
        setActiveSemester(index);
    };

    const handleCategoryItemSelect = (categoryId: number, itemId: number) => {
        setUserInteracted(true);
        setSelectedItemsByCategory({
            ...selectedItemsByCategory,
            [categoryId]: itemId
        });
    };

    // Variant 2 — light/maroon Figma design. Falls back to Variant 1 when unset/null.
    if (parsedData.layout_variant === "variant_2") {
        return (
            <ProgramStructure2Variant2
                parsedData={parsedData}
                semesters={semesters}
                activeSemester={activeSemester}
                onSemesterChange={handleSemesterChange}
                activeCategories={activeCategories}
                selectedItemsByCategory={selectedItemsByCategory}
                onCategoryItemSelect={handleCategoryItemSelect}
                openDropdown={openDropdown}
                onDropdownToggle={setOpenDropdown}
                activeSubjects={activeSubjects}
                totalCredits={totalCredits}
                expandedElective={expandedElective}
                onElectiveToggle={setExpandedElective}
                onCtaClick={handleCtaClick}
                isMobile={isMobile || false}
                isAnimationEnabled={isAnimationEnabled}
            />
        );
    }

    const bgColor = formatBackgroundStyle(parsedData, isMobile || false, "#000000");

    return (
        <section
            ref={sectionRef}
            className={cn(
                "relative w-full overflow-hidden flex justify-center",
                "px-[24px] py-[40px]",
                "sm:px-[60px] sm:py-[40px]",
                "md:px-[60px] md:py-[60px]",
                "lg:px-[80px] lg:py-[60px]",
                "xl:px-[140px] xl:py-[80px]"
            )}
            style={bgColor}
        >
            {/* Background Gradients */}
            <div className="absolute pointer-events-none inset-0 overflow-hidden">
                {parsedData.gradient_color_1 && (
                    <div
                        className="absolute w-[461px] h-[461px] xs:right-[-150px] xs:top-[-37px] sm:right-[-200px] sm:top-[-90px] lg:right-[-250px] lg:top-[-231px]"
                        style={{
                            background: `radial-gradient(circle, ${parsedData.gradient_color_1} 0%, transparent 70%)`,
                            opacity: 0.3
                        }}
                    />
                )}
                {parsedData.gradient_color_2 && (
                    <div
                        className="absolute w-[461px] h-[461px] xs:left-[-172px] xs:bottom-[100px] sm:left-[-172px] sm:bottom-[200px] lg:left-[-130px] lg:bottom-[300px]"
                        style={{
                            background: `radial-gradient(circle, ${parsedData.gradient_color_2} 0%, transparent 70%)`,
                            opacity: 0.3
                        }}
                    />
                )}
            </div>

            <div className={cn(
                "relative z-10 flex flex-col w-full",
                "max-w-[355px]",
                "sm:max-w-[960px]",
                "md:max-w-[1110px]",
                "lg:max-w-[1280px]",
                "xl:max-w-[1470px]"
            )}>
                {/* Title Section */}
                <TitleSection parsedData={parsedData} />

                {/* Description */}
                {parsedData.description && (
                    <Description parsedData={parsedData} />
                )}

                {/* Credits and Semester Tabs */}
                <CreditsAndSemesterTabs
                    parsedData={parsedData}
                    semesters={semesters}
                    activeSemester={activeSemester}
                    onSemesterChange={handleSemesterChange}
                />

                {/* Category Dropdowns */}
                {activeCategories.length > 0 && (
                    <RevealOnScroll once={true} className="relative z-30" y={0} x={-100} delay={100} enableAnimation={isAnimationEnabled}>
                    <CategoryDropdowns
                        categories={activeCategories}
                        selectedItemsByCategory={selectedItemsByCategory}
                        onCategoryItemSelect={handleCategoryItemSelect}
                        openDropdown={openDropdown}
                        onDropdownToggle={setOpenDropdown}
                    />
                    </RevealOnScroll>
                )}

                {/* Parent Container for Table and Tools/Skills */}
                <div className={cn(
                    "flex flex-col w-full mt-[24px]",
                    "gap-[24px]",
                    "sm:flex-row sm:gap-[20px] sm:mt-[20px]",
                    "md:flex-row md:gap-[28px] md:mt-[28px]",
                    "lg:flex-row lg:gap-[32px] lg:mt-[28px]",
                    "xl:flex-row xl:gap-[32px] xl:mt-[28px]"
                )}>
                    {/* Subjects Table */}
                    <SubjectsTable
                        parsedData={parsedData}
                        subjects={activeSubjects}
                        activeCategories={activeCategories}
                        activeSemester={semesters[activeSemester]}
                        expandedElective={expandedElective}
                        onElectiveToggle={setExpandedElective}
                        totalCredits={totalCredits}
                        animate={isAnimationEnabled && !userInteracted}
                    />

                    {/* Tools & Skills Section */}
                    <RevealOnScroll once={true} scaleStart={0.5} x={100} delay={100} enableAnimation={isAnimationEnabled}>
                    <ToolsAndSkillsSection parsedData={parsedData} />
                    </RevealOnScroll>
                </div>

                {/* Download Button */}
                {parsedData.cta_button_text && (
                    <RevealOnScroll once={true} enableAnimation={isAnimationEnabled}>
                    <DownloadButton
                        parsedData={parsedData}
                        onClick={handleCtaClick}
                    />
                    </RevealOnScroll>
                )}
            </div>
        </section>
    );
};
